import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AlertCircle, AlertTriangle, Filter, Play } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatWeekdayDateOrDash } from "@shared-core/domain/formatters";
import { readinessDisplayLabel } from "@shared-core/domain/readiness";
import { isScopeOverrideRole } from "@shared-core/domain/scopeOverride";
import {
  computeDefaultAlertsOnly,
  describeActiveJobsFilters,
  describePortfolioCountSummary,
  emptyJobsFilterValues,
  jobsFilterValuesToListOptions,
  parseJobsFilterValues,
  removeJobsFilterChip,
  writeJobsFilterValues,
  type JobsFilterLabelContext,
  type JobsFilterValues,
} from "@shared-core/domain/workPortfolioFilters";
import {
  fetchCustomerSuggestions,
  fetchJobSuggestions,
  gatherSuggestions,
  type SearchSuggestion,
} from "@shared-core/domain/searchSuggestions";
import { paths } from "@shared-core/routing/routes";
import { useAuth } from "../app/auth/hooks";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import { VisibilityProvenanceBadge } from "../app/components/domain/account-allocation/VisibilityProvenanceBadge";
import { FilterChip } from "../app/components/patterns/FilterChip";
import {
  JobsFilterDrawer,
  alertReasonLabel,
  dueWindowLabel,
  jobStatusLabel,
  quantityBandLabel,
  stageLabel,
  useWorkPortfolioColleagues,
  valueBandLabel,
} from "../app/components/domain/work-units/JobsFilterDrawer";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { SearchTypeahead } from "../app/components/patterns/SearchTypeahead";

const PAGE_SIZE = 25;

type SortOption =
  "due-asc" | "due-desc" | "booked-desc" | "booked-asc" | "value-desc";

const SORT_TO_PARAM: Record<SortOption, string> = {
  "due-asc": "required_at",
  "due-desc": "-required_at",
  "booked-desc": "-ordered_at",
  "booked-asc": "ordered_at",
  "value-desc": "-value",
};

const PARAM_TO_SORT: Record<string, SortOption> = {
  required_at: "due-asc",
  "-required_at": "due-desc",
  "-ordered_at": "booked-desc",
  ordered_at: "booked-asc",
  "-value": "value-desc",
};

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "due-asc", label: "Due date (soonest first)" },
  { value: "due-desc", label: "Due date (latest first)" },
  { value: "booked-desc", label: "Booked (newest first)" },
  { value: "booked-asc", label: "Booked (oldest first)" },
  { value: "value-desc", label: "Job value (highest)" },
];

// Universal severity-icon lexicon, matched against the work-unit detail page:
//   green   → Play (active, no open alerts)
//   amber   → AlertTriangle (warning — an open non-red alert)
//   red     → AlertTriangle (overdue — the only red-severity rule is
//             "Past due date", so red is now honestly overdue-only; the
//             detector that used to wrongly escalate amber to red off a raw
//             due_at timestamp has been fixed)
//
// `rag` here is the work-alert rollup (bool_or(severity = 'red') over the
// job's uncleared alerts, see work_portfolio_query.py) — it carries no due-
// date input on its own. When the row carries its own `topAlertLabel` (the
// top open work alert's own name), that wins as the accessible label —
// "Past due date" tells a screen-reader user more than the generic "Overdue".
function RagIcon({
  rag,
  topAlertLabel,
}: {
  rag: "green" | "amber" | "red";
  topAlertLabel?: string | null;
}) {
  if (rag === "red") {
    return (
      <AlertTriangle
        className="size-3.5 text-red-600 shrink-0"
        aria-label={topAlertLabel ?? "Overdue"}
      />
    );
  }
  if (rag === "amber") {
    return (
      <AlertTriangle
        className="size-3.5 text-amber-600 shrink-0"
        aria-label={topAlertLabel ?? "Warning"}
      />
    );
  }
  return (
    <Play
      className="size-3.5 text-emerald-600 shrink-0"
      aria-label="No alerts"
    />
  );
}

const RAG_ORDER: Record<"green" | "amber" | "red", number> = {
  green: 2,
  amber: 1,
  red: 0,
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatWeekdayDateOrDash(iso);
}

export function WorkPortfolioPage({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const customerFilter = searchParams.get("customer") || undefined;
  const seriesParent = searchParams.get("seriesParent");

  // The D4 default ("hide jobs with no open alerts") only applies when the
  // inbound URL didn't already name a filter of its own — computed once,
  // at mount, from the URL this page was actually opened with. See
  // computeDefaultAlertsOnly's doc comment: a link promising a specific
  // filtered population must never be narrowed further by a silent default.
  const [defaultAlertsOnly] = useState(() =>
    computeDefaultAlertsOnly(searchParams),
  );
  const [filters, setFilters] = useState<JobsFilterValues>(() =>
    parseJobsFilterValues(searchParams, defaultAlertsOnly),
  );
  const updateFilters = useCallback(
    (patch: Partial<JobsFilterValues>) =>
      setFilters((prev) => ({ ...prev, ...patch })),
    [],
  );
  const clearAllFilters = useCallback(
    () => setFilters(emptyJobsFilterValues()),
    [],
  );

  const [sortBy, setSortBy] = useState<SortOption>(
    () => PARAM_TO_SORT[searchParams.get("sort") ?? ""] ?? "due-asc",
  );
  const [page, setPage] = useState(() =>
    Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const canViewAsColleague = isScopeOverrideRole(user?.role);
  const colleagues = useWorkPortfolioColleagues(canViewAsColleague);

  // Typeahead suggestions: customers + jobs. A customer scopes the list to
  // that customer (clearing any typed text); a job jumps to its detail page.
  const fetchSuggestions = useCallback(
    (q: string) =>
      gatherSuggestions(
        [fetchCustomerSuggestions, fetchJobSuggestions],
        dataAdapter,
        q,
      ),
    [dataAdapter],
  );

  const selectSuggestion = useCallback(
    (s: SearchSuggestion) => {
      if (s.group === "Customers") {
        setSearchQuery("");
        const params = new URLSearchParams(searchParams);
        params.set("customer", s.id);
        params.set("page", "1");
        setSearchParams(params, { replace: true });
        return;
      }
      navigate(s.href);
    },
    [navigate, searchParams, setSearchParams],
  );

  const [data, setData] = useState<API.WorkPortfolioList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Any filter/sort/search change resets to page 1 so the user isn't
  // stranded on a page number that no longer exists under the new filter.
  useEffect(() => {
    setPage(1);
  }, [filters, sortBy, debouncedSearch, customerFilter]);

  // Mirror filters/sort/page/search into the URL — replacing, not pushing,
  // so filter changes don't spam browser history. This is the fix for
  // §2.2's desync bug: today `statuses` and `sort` seed once at mount and
  // never write back, and `q` isn't URL-backed at all.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    writeJobsFilterValues(next, filters, defaultAlertsOnly);
    const setOrDelete = (key: string, value: string | null) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    setOrDelete("sort", sortBy !== "due-asc" ? SORT_TO_PARAM[sortBy] : null);
    setOrDelete("page", page !== 1 ? String(page) : null);
    setOrDelete("q", debouncedSearch || null);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads searchParams only to preserve customer/seriesParent, which are set directly elsewhere; including it would loop
  }, [
    filters,
    defaultAlertsOnly,
    sortBy,
    page,
    debouncedSearch,
    setSearchParams,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await dataAdapter.getWorkPortfolio({
          ...jobsFilterValuesToListOptions(filters),
          customerId: customerFilter,
          seriesParent: seriesParent ?? undefined,
          q: debouncedSearch.trim() || undefined,
          sort: SORT_TO_PARAM[sortBy],
          page,
          pageSize: PAGE_SIZE,
        });
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          console.error("Error loading portfolio:", err);
          setError("Could not load portfolio.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [
    dataAdapter,
    filters,
    customerFilter,
    seriesParent,
    debouncedSearch,
    sortBy,
    page,
  ]);

  function dismissSeriesFilter() {
    const params = new URLSearchParams(searchParams);
    params.delete("seriesParent");
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  }

  function openSeries(parentJobNo: string) {
    const params = new URLSearchParams(searchParams);
    params.set("seriesParent", parentJobNo);
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  }

  function dismissCustomerFilter() {
    const params = new URLSearchParams(searchParams);
    params.delete("customer");
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  }

  const items = data?.items ?? [];
  const cards = data?.summary_cards;

  // Customer name for the scope chip, derived from the loaded rows.
  const customerName = customerFilter
    ? (items.find((r) => r.customer_id === customerFilter)?.customer_name ??
      null)
    : null;

  const seriesRollup =
    seriesParent && items.length > 0
      ? {
          count: items.length,
          worstRag: items.reduce<"green" | "amber" | "red">(
            (acc, r) => (RAG_ORDER[r.rag] < RAG_ORDER[acc] ? r.rag : acc),
            "green",
          ),
          totalExceptions: items.reduce(
            (sum, r) => sum + (r.exception_count ?? 0),
            0,
          ),
        }
      : null;

  const labelContext: JobsFilterLabelContext = {
    exceptionCodeLabel: alertReasonLabel,
    stageLabel,
    dueWindowLabel,
    valueBandLabel,
    quantityBandLabel,
    readinessLabel: readinessDisplayLabel,
    statusLabel: jobStatusLabel,
    viewingAsName:
      colleagues.find((c) => c.id === filters.viewingAsUserId)?.name ?? null,
  };
  const activeFilterChips = describeActiveJobsFilters(filters, labelContext);

  return (
    <div className="space-y-4">
      {/* Header — suppressed when embedded inside the JobsPage tabs. */}
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold">Work portfolio</h1>
          <p className="text-sm text-muted-foreground">
            Jobs in progress across your portfolio. Default sort by due date.
          </p>
        </div>
      )}

      {/* Search + Filters trigger — search stays on-page and above the
          fold; every other filter lives in the drawer. */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchTypeahead
          value={searchQuery}
          onValueChange={setSearchQuery}
          placeholder="Search job number, customer or title"
          ariaLabel="Search work portfolio"
          fetchSuggestions={fetchSuggestions}
          onSelectSuggestion={selectSuggestion}
          className="max-w-md flex-1"
        />
        <Button variant="outline" onClick={() => setFiltersOpen(true)}>
          <Filter className="size-4 mr-2" />
          {activeFilterChips.length > 0
            ? `Filters (${activeFilterChips.length})`
            : "Filters"}
        </Button>
      </div>

      {/* Active-filter chip row — one chip per filter value, individually
          removable, plus a customer scope chip and a series chip when
          those page-level (non-drawer) scopes are active. */}
      {(customerFilter || seriesParent || activeFilterChips.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {customerFilter && (
            <CustomerScopeChip
              customerName={customerName}
              onDismiss={dismissCustomerFilter}
            />
          )}
          {seriesParent && (
            <FilterChip
              label={
                seriesRollup
                  ? `Series: ${seriesParent} (${seriesRollup.count} job${seriesRollup.count === 1 ? "" : "s"})`
                  : `Series: ${seriesParent}`
              }
              onDismiss={dismissSeriesFilter}
              dismissLabel="Remove series filter"
            />
          )}
          {activeFilterChips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              onDismiss={() =>
                setFilters((prev) => removeJobsFilterChip(prev, chip.key))
              }
              dismissLabel={`Remove filter: ${chip.label}`}
            />
          ))}
          <button
            onClick={() => {
              clearAllFilters();
              if (customerFilter) dismissCustomerFilter();
              if (seriesParent) dismissSeriesFilter();
            }}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Readiness deliberately excludes finished work: the postgres_ro
          query adds `NOT IS_FINISHED_SQL` whenever readiness is set (see
          work_portfolio_query.py), and a contract test ties that count to
          the CSR home readiness bar. A filter that silently narrows the
          population on a second axis is exactly what this feature exists
          to prevent, so say so wherever the readiness filter is active. */}
      {filters.readiness && (
        <p className="text-xs text-muted-foreground">
          Finished jobs (produced or delivered) are excluded
        </p>
      )}

      {/* One-line count summary — replaces the four RAG stat cards; their
          numbers live here instead. Suppressed when the filtered result is
          empty: the empty-state card below already explains why, and a
          "0 of N jobs" line alongside it would only repeat that in a less
          specific way (see describePortfolioCountSummary's doc comment). */}
      {cards && data && data.total > 0 && (
        <p className="text-sm text-muted-foreground">
          {describePortfolioCountSummary(
            data.total,
            cards.total_active,
            cards.rag_amber,
            cards.rag_red,
          )}
        </p>
      )}

      {/* List */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          Loading…
        </Card>
      ) : error ? (
        <Card className="p-6 text-center text-sm text-red-700 border-red-200 bg-red-50">
          {error}
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          {activeFilterChips.length > 0
            ? `No jobs match. Try removing '${activeFilterChips[0].label}'.`
            : "No jobs match the current filter."}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-hover text-left">
                <tr>
                  <th className="p-2 font-medium w-6" aria-label="Status" />
                  <th className="p-2 font-medium">Job</th>
                  <th className="p-2 font-medium">Customer</th>
                  <th className="p-2 font-medium">Title</th>
                  <th className="p-2 font-medium">Stage</th>
                  <th className="p-2 font-medium">Booked</th>
                  <th className="p-2 font-medium">Due</th>
                  <th className="p-2 font-medium">Issues</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const openJob = () =>
                    navigate(paths.jobWorkspace({ jobNo: r.job_no }));
                  return (
                    // The row's internal id stays as the React key because
                    // one job_no can yield several rows here (series).
                    <tr
                      key={r.id}
                      onClick={openJob}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openJob();
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open job ${r.job_no}: ${r.title}`}
                      className="border-t border-border hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none cursor-pointer transition-colors"
                    >
                      <td className="p-2 w-6">
                        <RagIcon
                          rag={r.rag}
                          topAlertLabel={r.top_alert_label}
                        />
                      </td>
                      <td className="p-2 tabular-nums whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span>{r.job_no}</span>
                          {r.series_role !== "not_in_series" &&
                            r.series_parent_job_no &&
                            r.series_parent_job_no !== seriesParent && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSeries(r.series_parent_job_no!);
                                }}
                                aria-label={`Filter to series ${r.series_parent_job_no}`}
                                className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground border border-border rounded px-1 py-px"
                              >
                                series
                              </button>
                            )}
                        </div>
                      </td>
                      <td
                        className="p-2 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-1.5">
                          <Link to={r.customer_url} className="hover:underline">
                            {r.customer_name}
                          </Link>
                          <VisibilityProvenanceBadge
                            visibilityReason={r.visibility_reason}
                            coveringForUserName={r.covering_for_user_name}
                            coverUntil={r.cover_until}
                            className="shrink-0"
                          />
                        </div>
                      </td>
                      <td className="p-2 max-w-md">
                        <span className="truncate block">{r.title}</span>
                      </td>
                      <td className="p-2 text-muted-foreground whitespace-nowrap">
                        {r.current_state_phrase}
                      </td>
                      <td className="p-2 whitespace-nowrap tabular-nums text-muted-foreground">
                        {formatDate(r.ordered_at)}
                      </td>
                      <td className="p-2 whitespace-nowrap tabular-nums">
                        {formatDate(r.required_at)}
                      </td>
                      <td
                        className="p-2 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {r.open_issue_count > 0 ? (
                          <Link
                            to={`${paths.issues()}?customer=${encodeURIComponent(r.customer_id)}&status=open`}
                            className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 hover:underline"
                            aria-label={`${r.open_issue_count} open issue(s) for ${r.customer_name}`}
                          >
                            <AlertCircle className="size-3.5 shrink-0" />
                            <span className="tabular-nums">
                              {r.open_issue_count}
                              {r.open_complaint_count > 0
                                ? ` · ${r.open_complaint_count}✉`
                                : ""}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination — hidden when the result fits on one page */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-muted-foreground tabular-nums">
            Showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, data.total)} of {data.total}
          </div>
          <ListPagination
            page={page}
            totalPages={data.total_pages}
            onPageChange={setPage}
          />
        </div>
      )}

      <JobsFilterDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filters}
        onChange={updateFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterChips.length}
        canViewAsColleague={canViewAsColleague}
        colleagues={colleagues}
        sortValue={sortBy}
        sortOptions={SORT_OPTIONS}
        onSortChange={(v) => setSortBy(v as SortOption)}
      />
    </div>
  );
}
